// Parity P1 wave 1: task layout, field requirements, field history, watchers and task metric
// observations (hybrid design §3.1 properties and Metrics block, §3.3 layout editor; packages
// doc "P1" API). Handlers: services/api routes/work/task-layout.ts (layouts, requirements) and
// routes/work/task-detail.ts (history, watchers, observations).
//
// - A task layout is a board's (managers of the board edit it) or a space's default (managers
//   of the space); a board without its own layout uses its space's default, else the built-in
//   default. Every save is a new version (`task_layout_version`, history keeps them all; the
//   editor lists the newest five); `layout: null` resets a board to its space default. Saves
//   carry `base_version` (the version the editor opened; 409 `conflict` when another save won).
// - Core properties (title, tags, status, assignee, delegate, dates, priority) are fixed and
//   never placed (design D6); agents never edit layouts (the routes refuse agent principals).
// - A field's `required_on` is enforced by the status writer: a task cannot reach a done status
//   while a field required on completion is empty (422 `required_missing`, `missing` = ids).
// - Task observations are a metric's time series on one task: append-only, a correction names
//   the row it `supersedes_id`; the number field named after the metric (if the board has one)
//   mirrors the latest value for older readers.
import { z } from "zod";
import { Actor } from "../actor";
import { Timestamp, text, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

/** A block a layout places (`description`, `subtasks`, `field:<uuid>`, `fields:rest`, …). */
export const TaskLayoutBlockId = z
  .string()
  .max(80)
  .regex(/^[a-z][a-z0-9_:.-]{0,79}$/)
  .describe("A registered block id (`description`, `field:<uuid>`, `fields:rest`, …)");

export const TaskLayoutGroup = z
  .strictObject({
    group: z.strictObject({
      title: text(80),
      icon: z.string().max(40).optional(),
      accent: z.string().max(32).optional(),
      blocks: z.array(TaskLayoutBlockId).max(40),
    }),
  })
  .meta({ id: "TaskLayoutGroup" });

/** A column's item: a block, or one titled group of blocks (one nesting level, as Work OS). */
export const TaskLayoutItem = z.union([TaskLayoutBlockId, TaskLayoutGroup]);

export const TaskLayoutColumn = z
  .strictObject({
    /** Of 12; a section's columns need not add up (the host wraps). */
    width: z.int().min(2).max(12),
    items: z.array(TaskLayoutItem).max(40),
  })
  .meta({ id: "TaskLayoutColumn" });

export const TaskLayoutSection = z
  .strictObject({
    id: z
      .string()
      .max(40)
      .regex(/^[a-z0-9][a-z0-9_-]{0,39}$/)
      .describe("Stable within the layout; the per-person collapse pref names it"),
    title: text(80).optional(),
    icon: z.string().max(40).optional(),
    accent: z.string().max(32).optional(),
    hidden: z.boolean().optional(),
    columns: z.array(TaskLayoutColumn).min(1).max(4),
  })
  .meta({ id: "TaskLayoutSection" });

export const TaskLayout = z
  .strictObject({
    version: z.literal(1).describe("The schema version of the layout document"),
    sections: z.array(TaskLayoutSection).max(20),
    /** Blocks deliberately left out (the editor's shelf shows every unplaced block anyway). */
    hidden: z.array(TaskLayoutBlockId).max(200),
  })
  .meta({ id: "TaskLayout" });
export type TaskLayout = z.infer<typeof TaskLayout>;

/**
 * The built-in layout (a board without its own and a space without a default render this).
 * Fields not placed in the body render in the properties column, so it places no `fields:rest`.
 */
export const DEFAULT_TASK_LAYOUT: TaskLayout = {
  version: 1,
  sections: [
    {
      id: "overview",
      title: "Overview",
      columns: [{ width: 12, items: ["description", "checklists"] }],
    },
    {
      id: "delivery",
      title: "Delivery",
      columns: [{ width: 12, items: ["subtasks", "dependencies", "links", "files", "time"] }],
    },
    { id: "metrics", title: "Metrics", columns: [{ width: 12, items: ["metrics"] }] },
  ],
  hidden: [],
};

export const FieldRequiredOn = z.enum(["none", "create", "complete", "both", "enter"]);

/** BOARD-GATES: the statuses an `enter` rule holds (required on entering any of them). */
const RequiredStatuses = z
  .array(z.string().min(1).max(64))
  .max(30)
  .describe("`required_on: enter` only: the status keys whose entry needs the value");

/** A field's requirement and visibility rule (`field_def.required_on`, `always_show`). */
export const FieldRule = z
  .object({
    field_def_id: Uuid,
    required_on: FieldRequiredOn,
    required_statuses: RequiredStatuses.optional(),
    always_show: z.boolean().describe("Shown even when empty (otherwise behind “+ Set a field”)"),
    show_on_card: z
      .boolean()
      .describe("On the cards of every view that does not name its own card fields"),
  })
  .meta({ id: "FieldRule" });
export type FieldRule = z.infer<typeof FieldRule>;

export const TaskLayoutSource = z.enum(["board", "space", "default"]);

/** One saved version of a scope's layout (`layout: null`: a reset to the space default). */
export const TaskLayoutVersion = z
  .object({
    version: z.int().min(1),
    layout: TaskLayout.nullable(),
    saved_by: Actor,
    saved_at: Timestamp,
  })
  .meta({ id: "TaskLayoutVersion" });

/** A board's layout as the drawer and the editor read it. */
export const BoardTaskLayout = z
  .object({
    board_id: Uuid,
    space_id: Uuid,
    /** The board's own layout (null: it uses the space default or the built-in one). */
    layout: TaskLayout.nullable(),
    /** The board's current version (0: never saved). Saves send it back as `base_version`. */
    version: z.int().min(0),
    /** What the drawer renders. */
    effective: TaskLayout,
    source: TaskLayoutSource,
    fields: z.array(FieldRule).describe("Every field of the board, with its rule"),
    can_edit: z.boolean().describe("The caller manages the board"),
  })
  .meta({ id: "BoardTaskLayout" });
export type BoardTaskLayout = z.infer<typeof BoardTaskLayout>;

/** A board a space default reaches (`uses_default`: it has no layout of its own). */
export const TaskLayoutBoardUse = z
  .object({ board_id: Uuid, name: z.string(), uses_default: z.boolean() })
  .meta({ id: "TaskLayoutBoardUse" });

export const SpaceTaskLayout = z
  .object({
    space_id: Uuid,
    layout: TaskLayout.nullable(),
    version: z.int().min(0),
    effective: TaskLayout,
    /** The space's live boards: those with `uses_default` change when the default is saved. */
    boards: z.array(TaskLayoutBoardUse),
    can_edit: z.boolean().describe("The caller manages the space"),
  })
  .meta({ id: "SpaceTaskLayout" });
export type SpaceTaskLayout = z.infer<typeof SpaceTaskLayout>;

export const TaskLayoutPut = z
  .strictObject({
    layout: TaskLayout.nullable().describe("null resets a board to its space default"),
    base_version: z.int().min(0).describe("The version the editor opened (409 when stale)"),
  })
  .meta({ id: "TaskLayoutPut" });

export const TaskLayoutRestore = z
  .strictObject({ version: z.int().min(1), base_version: z.int().min(0) })
  .meta({ id: "TaskLayoutRestore" });

export const TaskLayoutHistory = z
  .object({ data: z.array(TaskLayoutVersion).describe("Newest first, at most five") })
  .meta({ id: "TaskLayoutHistory" });

export const FieldRulePatch = z
  .strictObject({
    required_on: FieldRequiredOn.optional(),
    required_statuses: RequiredStatuses.optional(),
    always_show: z.boolean().optional(),
    show_on_card: z.boolean().optional(),
  })
  .meta({ id: "FieldRulePatch" });

/** One change of a field's value on a task (from `task.field_changed`). */
export const FieldHistoryEntry = z
  .object({
    event_id: Uuid,
    field_def_id: Uuid,
    from: z.unknown().nullable(),
    to: z.unknown().nullable(),
    at: Timestamp,
    actor: Actor,
  })
  .meta({ id: "FieldHistoryEntry" });

export const FieldHistoryQuery = z.object({ field_id: Uuid });
export const FieldHistory = z
  .object({ data: z.array(FieldHistoryEntry).describe("Newest first, at most 100") })
  .meta({ id: "FieldHistory" });

export const WatcherReason = z.enum(["follow", "assignee", "creator"]);

/** Who gets the task's changes in their inbox, and why. */
export const TaskWatcher = z
  .object({
    membership_id: Uuid,
    name: z.string(),
    kind: z.enum(["person", "agent"]),
    reasons: z.array(WatcherReason).min(1),
  })
  .meta({ id: "TaskWatcher" });
export type TaskWatcher = z.infer<typeof TaskWatcher>;

export const TaskWatchers = z
  .object({
    data: z.array(TaskWatcher),
    /** The caller: whether the bell reads "Following", and whether through an explicit follow. */
    me: z.object({ following: z.boolean(), explicit: z.boolean() }),
  })
  .meta({ id: "TaskWatchers" });
export type TaskWatchers = z.infer<typeof TaskWatchers>;

export const TaskObservation = z
  .object({
    id: Uuid,
    task_id: Uuid,
    metric: z.string(),
    value: z.number(),
    unit: z.string().nullable(),
    note: z.string().nullable(),
    observed_at: Timestamp,
    supersedes_id: Uuid.nullable(),
    /** Superseded by a later correction (kept; the series reads live rows). */
    superseded: z.boolean(),
    actor: Actor,
    created_at: Timestamp,
  })
  .meta({ id: "TaskObservation" });
export type TaskObservation = z.infer<typeof TaskObservation>;

export const TaskObservationCreate = z
  .strictObject({
    metric: z
      .string()
      .min(1)
      .max(80)
      .regex(/^[A-Za-z0-9 _.%-]+$/),
    value: z.number().finite(),
    unit: z.string().max(20).optional(),
    note: z.string().max(2000).optional(),
    observed_at: Timestamp.optional().describe("When the value was true (default now)"),
    supersedes_id: Uuid.optional().describe("The live row this corrects"),
  })
  .meta({ id: "TaskObservationCreate" });

export const TaskObservationQuery = z.object({ metric: z.string().min(1).max(80).optional() });
export const TaskObservations = z
  .object({
    data: z
      .array(TaskObservation)
      .describe(
        "Oldest first, at most 500: the newest live readings first, superseded ones only as far as they fit after them",
      ),
  })
  .meta({ id: "TaskObservations" });

/** `extensions.*` P1 adds to `GET tasks/{id}` (camelCased on `get_task`). */
export const TaskP1Extensions = {
  watchers: z.array(TaskWatcher),
  required_missing: z.array(
    z.object({ field_def_id: Uuid, name: z.string(), required_on: FieldRequiredOn }),
  ),
  layout_sections: z.array(
    z.object({ id: z.string(), title: z.string().nullable(), blocks: z.array(z.string()) }),
  ),
  estimate_minutes: z.int().min(0).nullable(),
};

const SpaceItemParams = CompanyItemParams;
const layoutErrors = ["conflict"] as const;

export const taskDetailRoutes = {
  "taskLayout.board.get": defineRoute({
    ...core,
    id: "taskLayout.board.get",
    method: "get",
    path: `${base}/boards/{id}/task-layout`,
    tag: "task-layout",
    summary: "A board's task layout (its own, or the space default it uses) and its field rules",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: BoardTaskLayout },
    errors: [],
    emits: [],
  }),
  "taskLayout.board.put": defineRoute({
    ...core,
    id: "taskLayout.board.put",
    method: "put",
    path: `${base}/boards/{id}/task-layout`,
    tag: "task-layout",
    summary: "Save a board's task layout as a new version (null: use the space default)",
    access: "member",
    params: CompanyItemParams,
    body: TaskLayoutPut,
    response: { status: 200, schema: BoardTaskLayout },
    errors: layoutErrors,
    emits: ["board.task_layout_changed"],
  }),
  "taskLayout.board.history": defineRoute({
    ...core,
    id: "taskLayout.board.history",
    method: "get",
    path: `${base}/boards/{id}/task-layout/history`,
    tag: "task-layout",
    summary: "A board's newest five task layout versions",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: TaskLayoutHistory },
    errors: [],
    emits: [],
  }),
  "taskLayout.board.restore": defineRoute({
    ...core,
    id: "taskLayout.board.restore",
    method: "post",
    path: `${base}/boards/{id}/task-layout/restore`,
    tag: "task-layout",
    summary: "Save an earlier version of a board's task layout as its newest",
    access: "member",
    params: CompanyItemParams,
    body: TaskLayoutRestore,
    response: { status: 200, schema: BoardTaskLayout },
    errors: layoutErrors,
    emits: ["board.task_layout_changed"],
  }),
  "taskLayout.space.get": defineRoute({
    ...core,
    id: "taskLayout.space.get",
    method: "get",
    path: `${base}/spaces/{id}/task-layout`,
    tag: "task-layout",
    summary: "A space's default task layout and the boards that use it",
    access: "member",
    params: SpaceItemParams,
    response: { status: 200, schema: SpaceTaskLayout },
    errors: [],
    emits: [],
  }),
  "taskLayout.space.put": defineRoute({
    ...core,
    id: "taskLayout.space.put",
    method: "put",
    path: `${base}/spaces/{id}/task-layout`,
    tag: "task-layout",
    summary: "Save a space's default task layout as a new version (null: the built-in default)",
    access: "member",
    params: SpaceItemParams,
    body: TaskLayoutPut,
    response: { status: 200, schema: SpaceTaskLayout },
    errors: layoutErrors,
    emits: ["space.default_task_layout_changed"],
  }),
  "taskLayout.space.history": defineRoute({
    ...core,
    id: "taskLayout.space.history",
    method: "get",
    path: `${base}/spaces/{id}/task-layout/history`,
    tag: "task-layout",
    summary: "A space's newest five default task layout versions",
    access: "member",
    params: SpaceItemParams,
    response: { status: 200, schema: TaskLayoutHistory },
    errors: [],
    emits: [],
  }),
  "taskLayout.space.restore": defineRoute({
    ...core,
    id: "taskLayout.space.restore",
    method: "post",
    path: `${base}/spaces/{id}/task-layout/restore`,
    tag: "task-layout",
    summary: "Save an earlier version of a space's default task layout as its newest",
    access: "member",
    params: SpaceItemParams,
    body: TaskLayoutRestore,
    response: { status: 200, schema: SpaceTaskLayout },
    errors: layoutErrors,
    emits: ["space.default_task_layout_changed"],
  }),
  "fields.rule": defineRoute({
    ...core,
    id: "fields.rule",
    method: "patch",
    path: `${base}/fields/{id}/rule`,
    tag: "task-layout",
    summary: "Set a field's requirement stage and whether it always shows (board managers)",
    access: "member",
    params: CompanyItemParams,
    body: FieldRulePatch,
    response: { status: 200, schema: FieldRule },
    errors: [],
    emits: ["field.required_changed"],
  }),
  "tasks.fieldHistory": defineRoute({
    ...core,
    id: "tasks.fieldHistory",
    method: "get",
    path: `${base}/tasks/{id}/field-history`,
    tag: "tasks",
    summary: "The changes of one field's value on a task, newest first, with who made them",
    access: "member",
    params: CompanyItemParams,
    query: FieldHistoryQuery,
    response: { status: 200, schema: FieldHistory },
    errors: [],
    emits: [],
  }),
  "tasks.watchers": defineRoute({
    ...core,
    id: "tasks.watchers",
    method: "get",
    path: `${base}/tasks/{id}/watchers`,
    tag: "tasks",
    summary: "Who watches a task (followers, assignees, the creator) and whether the caller does",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: TaskWatchers },
    errors: [],
    emits: [],
  }),
  "tasks.observations.list": defineRoute({
    ...core,
    id: "tasks.observations.list",
    method: "get",
    path: `${base}/tasks/{id}/observations`,
    tag: "tasks",
    summary: "A task's metric observations (every row, superseded ones marked)",
    access: "member",
    params: CompanyItemParams,
    query: TaskObservationQuery,
    response: { status: 200, schema: TaskObservations },
    errors: [],
    emits: [],
  }),
  "tasks.observations.create": defineRoute({
    ...core,
    id: "tasks.observations.create",
    method: "post",
    path: `${base}/tasks/{id}/observations`,
    tag: "tasks",
    summary: "Record a metric observation on a task (or correct one with `supersedes_id`)",
    access: "member",
    params: CompanyItemParams,
    body: TaskObservationCreate,
    response: { status: 201, schema: TaskObservation },
    errors: ["conflict"],
    emits: ["task.observation_recorded", "task.field_changed"],
  }),
} as const;
